import { API_URL } from '../../constants';
import { TAudio, TConferenceProgress, TUserGroup } from '../../../spa/store/models/types';
import { deleteRequest, getRequest, postRequest } from '../../utils/requestService';
import { getDispatch } from '../../../spa/store';

export interface IRestAudioGroup extends TAudio {
  audio: TAudio;
}

interface IRestAudioCaptionWord {
  text: string,
  time: number,
  startLetterIndex?: number,
  endLetterIndex?: number
}

export interface IRestAudioCaption {
  startTime: number,
  endTime: number,
  words: IRestAudioCaptionWord[],
  captionText?: string,
  userId?: number;
  userName?: string;
}

export type TCaptionProvider = string | null; // null = legacy set

export interface IRestProviderCaptions {
  provider: TCaptionProvider,
  captions: IRestAudioCaption[],
}

export type TRestAudioAddResult = TAudio & {
  error?: string
}

export type TRecordingInfoPatch = {
  title?: string;
  description?: string;
};

// title/description as the list shows them: override ?? shared, the native copy of the shared one,
// and whether each field is the user's own (then the tile shows it in both modes)
export type TRecordingInfo = {
  title: string;
  description: string | null;
  hasOwnTitle: boolean;
  hasOwnDescription: boolean;
  titleNative: string | null;
  descriptionNative: string | null;
};

// Cancel of the popup's Regenerates: 'gone' = the server no longer has the previous state
export type TRestoreInfoResult = 'restored' | 'gone' | 'failed';

export interface ICreateBookParams {
  targetLang?: string;
  nativeLang?: string;
}

export class AudioRest {

  private static FETCH_URL = API_URL + '/audio/info';
  private static GET_CAPTIONS_URL = API_URL + '/audio/captions';
  private static DOWNLOAD_URL_URL = API_URL + '/audio/downloadUrl';
  private static ADD_URL = API_URL + '/audio/add';
  private static REMOVE_URL = API_URL + '/audio/remove';
  private static DELETE_MEDIA_URL = API_URL + '/audio/deleteMedia';
  private static UPDATE_URL = API_URL + '/audio/update';
  private static READY_BY_ROOM_URL = API_URL + '/audio/ready';
  private static BOOK_URL = API_URL + '/audio/book';
  private static BOOK_ID_URL = API_URL + '/audio/bookId';
  private static INFO_URL = API_URL + '/audio/info';

  public static async fetch(audioId: number, groupId: number): Promise<IRestAudioGroup | null> {
    const dispatch = getDispatch();
    const url = `${AudioRest.FETCH_URL}/${groupId}/${audioId}`;
    return await dispatch(getRequest(url));
  }

  // The response is always grouped: `[{ provider, captions }]`. Without `provider` the array
  // holds only the legacy set (`provider: null`); `provider=all` returns every set.
  public static async getCaptions(audioId: number, provider?: string): Promise<IRestProviderCaptions[] | null> {
    const dispatch = getDispatch();
    const query = provider ? `?provider=${encodeURIComponent(provider)}` : '';
    const url = `${AudioRest.GET_CAPTIONS_URL}/${audioId}${query}`;
    return await dispatch(getRequest(url));
  }

  // Deletes one provider's caption set
  public static async deleteCaptions(audioId: number, provider: TCaptionProvider): Promise<{ provider: TCaptionProvider; deleted: number } | null> {
    const dispatch = getDispatch();
    const p = provider === null ? 'null' : encodeURIComponent(provider);
    const url = `${AudioRest.GET_CAPTIONS_URL}/${audioId}?provider=${p}`;
    return await dispatch(deleteRequest(url));
  }

  // Convenience for the common case: the default caption set (the configured provider's, with the
  // server's legacy fallback) as a flat array. Without `?provider` the response is a single group.
  public static async getDefaultCaptions(audioId: number): Promise<IRestAudioCaption[]> {
    return (await AudioRest.getCaptions(audioId))?.[0]?.captions || [];
  }

  // Returns a presigned S3 URL that responds with `Content-Disposition: attachment`
  // so the file is downloaded by the browser rather than opened inline.
  public static async getDownloadUrl(audioId: number): Promise<string | null> {
    const dispatch = getDispatch();
    const url = `${AudioRest.DOWNLOAD_URL_URL}/${audioId}`;
    const result = await dispatch(getRequest(url));
    return result?.url || null;
  }

  public static async addAudio(text: string, groupId: number, audio: boolean, langCode?: string): Promise<TRestAudioAddResult> {
    const dispatch = getDispatch();
    return await dispatch(postRequest(AudioRest.ADD_URL, {text, groupId, langCode, audio}));
  }

  public static async updateAudio(id: number, data: Partial<TAudio>): Promise<any> {
    const dispatch = getDispatch();
    return await dispatch(postRequest(AudioRest.UPDATE_URL, {id, data}));
  }

  public static async removeAudio(audioId: number, groupId: number): Promise<any> {
    const dispatch = getDispatch();
    return await dispatch(postRequest(AudioRest.REMOVE_URL, {audioId, groupId}));
  }

  // Manual "Delete audio"/"Delete video" from the content-library menu — wipes just that media type
  // (processed file + raw sources) from an already-processed call recording, leaving the rest intact.
  public static async deleteMedia(audioId: number, media: 'audio' | 'video'): Promise<TAudio | null> {
    const dispatch = getDispatch();
    return await dispatch(postRequest(AudioRest.DELETE_MEDIA_URL, {audioId, media}));
  }

  public static async readyByRoom(roomId: string): Promise<{
    ready: boolean;
    group?: TUserGroup;
    progress?: TConferenceProgress;
    finished?: boolean;
  }> {
    const dispatch = getDispatch();
    const url = `${AudioRest.READY_BY_ROOM_URL}/${roomId}`;
    return await dispatch(getRequest(url));
  }

  // Creates (or returns the already created) endless-book book from this recording's transcript —
  // page 1 is generated within the request, so it can take a minute.
  public static async createBook(audioId: number, params: ICreateBookParams): Promise<{ bookId: number }> {
    const dispatch = getDispatch();
    const url = `${AudioRest.BOOK_URL}/${audioId}`;
    return await dispatch(postRequest(url, params));
  }

  // Clears the stored book id (bookId: null) once the reader finds the book gone.
  public static async saveBookId(audioId: number, bookId: number | null): Promise<void> {
    const dispatch = getDispatch();
    const url = `${AudioRest.BOOK_ID_URL}/${audioId}`;
    await dispatch(postRequest(url, { bookId }));
  }

  // Per-user title/description; an empty string resets to the shared value, an absent field is left
  // as is. Returns the effective values of both.
  public static async saveInfo(audioId: number, info: TRecordingInfoPatch): Promise<TRecordingInfo> {
    const dispatch = getDispatch();
    const url = `${AudioRest.INFO_URL}/${audioId}`;
    return await dispatch(postRequest(url, info));
  }

  // AI title/description from the captions (works without audio); resets the caller's own override.
  // Translates the native copies in the same request. first: the first Regenerate of a settings-popup
  // session — restoreInfo goes back to the state before it.
  public static async generateInfo(audioId: number, first: boolean): Promise<TRecordingInfo & { nativeFailed?: boolean }> {
    const dispatch = getDispatch();
    const url = `${AudioRest.INFO_URL}/${audioId}/generate`;
    return await dispatch(postRequest(url, { first }));
  }

  // Cancel of the popup's Regenerates: shared text and every participant's copy back as they were.
  public static async restoreInfo(audioId: number): Promise<TRecordingInfo> {
    const dispatch = getDispatch();
    const url = `${AudioRest.INFO_URL}/${audioId}/restore`;
    return await dispatch(postRequest(url, {}));
  }

}
